import type { EventSummary } from '@mmef/shared/api';
import { describeAgeRange, describePrice } from '@mmef/shared/api';
import { formatDate } from '@mmef/shared/lib';

/**
 * Roughly where Google truncates a description in a result. Not a hard limit — the whole string is
 * still read for matching — but the part past it is invisible, so the useful facts go first.
 */
const DESCRIPTION_BUDGET = 155;

/**
 * The title a search result shows, and what a browser tab says.
 *
 * <p>The event's own title already names the kind of event and the city, so the date is what this
 * adds: two Birmingham events a month apart are otherwise indistinguishable in a list of results,
 * and "is it soon" is the first thing anyone wants to know.
 *
 * <p>No site name appended. These titles run to sixty characters already, and a suffix would push
 * the city — the part someone is searching for — past where it stops being shown.
 */
export function eventTitle(event: EventSummary): string {
  return `${event.title}, ${formatDate(event.startTime, event.timezone)}`;
}

/**
 * The grey text under the title in a search result.
 *
 * <p>Built from facts rather than the event's own description, which opens with "Format" and a
 * list of rotation timings — true, but not what persuades someone to click. Where, when, who it's
 * for and what it costs are the questions a result has to answer.
 *
 * <p>Assembled from whatever the event actually has: most of the catalogue states no age policy,
 * and several state no price. A sentence about "all ages" on an event whose organiser said nothing
 * about ages would be inventing a claim, so absent facts are simply left out rather than filled in.
 */
export function eventDescription(event: EventSummary): string {
  const where = [event.venueName, event.city].filter(Boolean).join(', ');
  const parts: string[] = [];

  parts.push(where
    ? `${formatDate(event.startTime, event.timezone)} at ${where}.`
    : `${formatDate(event.startTime, event.timezone)}.`);

  if (event.organiser) parts.push(`Organised by ${event.organiser}.`);
  if (event.ageRanges.length > 0) {
    parts.push(`Ages ${event.ageRanges.map(describeAgeRange).join(', ')}.`);
  }

  const price = describePrice(event.price);
  if (price) parts.push(price === 'Free' ? 'Free to attend.' : `Tickets ${price}.`);

  // Whole sentences only. A description cut mid-word reads as broken rather than abbreviated, and
  // the last fact is always the least important one — losing it costs nothing.
  let out = '';
  for (const part of parts) {
    const next = out ? `${out} ${part}` : part;
    if (next.length > DESCRIPTION_BUDGET) break;
    out = next;
  }
  return out;
}
